import * as THREE from 'three';
import { smooth } from './critter-math.js';
import { bakeCritter, parts } from './critter-parts.js';

export type RabbitMood = 'rest' | 'groom' | 'alert' | 'graze' | 'sleep';

export interface RabbitAction {
  /** 1 when sitting, 0 when hopping; eased by the brain so a rabbit gathers itself before and after a hop. */
  settle: number;
  /** Where in the current hop, 0–1 (the pose at 0 and 1 is the same, so hops chain), and how hard it goes: 0 foraging, 1 bolting. */
  hop: number; bolt: number;
  /** What it is doing while sat, the mood before it (cross-faded over `moodTime` seconds) and the bird's own idle clock. */
  mood: RabbitMood; previous: RabbitMood; moodTime: number; idle: number;
}
export const restingRabbit = (): RabbitAction => ({ settle: 1, hop: 0, bolt: 0, mood: 'rest', previous: 'rest', moodTime: 9, idle: 0 });

const FUR = [0x8a7461, 0x7d7a73, 0xa08560];
const BELLY = 0xe6dfd0, EAR_INNER = 0xcb9d93, EAR_TIP = 0x4a3d33, NOSE = 0xd0847c, EYE = 0x15110f;
/** Hip height with the haunches resting, in the model's own metres (a rabbit is about 0.4 m long). */
export const RABBIT_HIP_HEIGHT = .085;

/** Joint angles and offsets for one instant; poses are blended field by field. */
interface Pose { lift: number; pitch: number; crouch: number; head: number; yaw: number; ear: number; splay: number; nose: number; front: number; hind: number; chew: number }
const FIELDS: (keyof Pose)[] = ['lift', 'pitch', 'crouch', 'head', 'yaw', 'ear', 'splay', 'nose', 'front', 'hind', 'chew'];
const mix = (a: Pose, b: Pose, t: number): Pose => { const out = {} as Pose; for (const key of FIELDS) out[key] = a[key] + (b[key] - a[key]) * t; return out; };

/** Sniffing comes in bursts: a slow gate over a fast twitch. */
const sniff = (t: number, rate: number) => smooth((Math.sin(t * .9) + .4) * 1.6) * (.5 + .5 * Math.sin(t * rate));

function sitPose(mood: RabbitMood, t: number): Pose {
  const look = Math.sin(t * .7) * .5 + Math.sin(t * 1.9 + 1) * .2;
  switch (mood) {
    case 'alert': // Up on the haunches, forepaws tucked at the chest, ears straight up and swivelling.
      return { lift: 0, pitch: -.95, crouch: 0, head: -.28, yaw: Math.sin(t * .4) * .8, ear: .1, splay: .08, nose: .5 + .5 * Math.sin(t * 22), front: -.85, hind: 0, chew: 0 };
    case 'groom': // Forepaws sweep over the face, ears laid flat.
      return { lift: 0, pitch: -.55, crouch: 0, head: .6, yaw: Math.sin(t * 9) * .1, ear: -.7, splay: .1, nose: .4, front: -1.5 + Math.sin(t * 9) * .25, hind: 0, chew: 0 };
    case 'graze': // Nose down, nibbling.
      return { lift: 0, pitch: .1, crouch: 0, head: .32, yaw: Math.sin(t * .5) * .15, ear: -.5, splay: .3, nose: .7 + .3 * Math.sin(t * 15), front: -.1, hind: 0, chew: .5 + .5 * Math.sin(t * 11) };
    case 'sleep': // Loafed down with the head tucked, ears flat, breathing slowly.
      return { lift: 0, pitch: .12, crouch: 0, head: .7, yaw: 0, ear: -.9, splay: .04, nose: .15 + .1 * Math.sin(t * 1.2), front: -.1, hind: 0, chew: 0 };
    default: // Sat up easy, breathing, looking about, with bursts of sniffing.
      return { lift: 0, pitch: -.42 + Math.sin(t * 2.2) * .012, crouch: 0, head: .1 + Math.sin(t * .5) * .04, yaw: look * .5, ear: Math.sin(t * .31) * .08, splay: .22, nose: sniff(t, 17), front: -.55, hind: 0, chew: 0 };
  }
}

/** A single hop. Poses at 0 and 1 are identical so hops chain: gather, spring, glide, reach, absorb. */
function hopPose(s: number, bolt: number): Pose {
  const height = .09 + .13 * bolt, rest = .25, airborne = s > .14 && s < .72, u = Math.min(1, Math.max(0, (s - .14) / .58));
  let crouch: number;
  if (s < .14) crouch = rest + (.6 - rest) * smooth(s / .14);
  else if (airborne) crouch = .6 * (1 - smooth(u / .25));
  else if (s < .86) crouch = .7 * smooth((s - .72) / .14);
  else crouch = .7 + (rest - .7) * smooth((s - .86) / .14);
  const lift = airborne ? height * 4 * u * (1 - u) : 0;
  // Nose up on the way up and down on the way back; level on the ground.
  const pitch = airborne ? (u - .5) * .77 * (1 + bolt * .5) : 0;
  // Hind feet stay flat while gathering, drive back and up behind the body, then swing forward to land flat again; forepaws tuck mid-hop and reach for the ground.
  const drive = 2.9 * smooth((u - .12) / .35);
  const hind = s < .14 ? 0 : airborne ? drive + (.15 - drive) * smooth((u - .6) / .4) : .15 * (1 - smooth((s - .72) / .28));
  const front = s < .14 ? -.3 * smooth(s / .14) : airborne ? -.3 - .7 * Math.sin(Math.PI * u) : -.3 * (1 - smooth((s - .72) / .28));
  // Ears stream back while the hop is under way.
  const streaming = smooth(s / .14) * (1 - smooth((s - .72) / .28));
  return { lift, pitch, crouch, head: -pitch * .5 + .05, yaw: 0, ear: -(.3 + .7 * bolt) * streaming - Math.sin(Math.PI * u) * .25, splay: .2 - bolt * .12, nose: .35, front, hind, chew: 0 };
}

/** A low-poly cottontail facing +Z: haunches, long ears on their own pivots, a twitching muzzle, forepaws and hind feet that work independently. */
export class RabbitModel {
  readonly root = new THREE.Group();
  private hips = new THREE.Group(); private neck = new THREE.Group();
  private ears: THREE.Group[] = []; private fronts: THREE.Group[] = []; private hinds: THREE.Group[] = [];
  private muzzle: THREE.Mesh;

  /** `tone` picks one of the coat colours; `seed` offsets the idle clock. */
  constructor(tone = 0, private seed = 0) {
    const fur = FUR[tone % FUR.length], { hips, neck, root } = this;
    hips.position.set(0, RABBIT_HIP_HEIGHT, -.09); root.add(hips);
    parts.blob(hips, fur, [.075, .07, .16], [0, .035, .1]);
    parts.blob(hips, BELLY, [.05, .035, .1], [0, 0, .1]);
    parts.blob(hips, BELLY, [.038, .038, .038], [0, .045, -.075]);
    for (const side of [-1, 1]) parts.blob(hips, fur, [.055, .07, .095], [side * .066, .015, 0]);
    neck.position.set(0, .07, .2); hips.add(neck);
    parts.blob(neck, fur, [.055, .052, .075], [0, .02, .05]);
    this.muzzle = parts.blob(neck, BELLY, [.032, .028, .035], [0, -.012, .115]);
    parts.blob(neck, NOSE, [.011, .01, .01], [0, -.002, .148]);
    for (const side of [-1, 1]) {
      parts.blob(neck, EYE, [.011, .012, .011], [side * .042, .032, .068]);
      const ear = new THREE.Group(); ear.position.set(side * .026, .058, .03); neck.add(ear);
      parts.blob(ear, fur, [.018, .068, .01], [0, .06, 0]);
      parts.blob(ear, EAR_INNER, [.011, .054, .005], [0, .058, .007]);
      parts.blob(ear, EAR_TIP, [.015, .016, .01], [0, .118, 0]);
      this.ears.push(ear);
      const front = new THREE.Group(); front.position.set(side * .04, .04, .15); hips.add(front);
      parts.blob(front, fur, [.015, .062, .016], [0, -.058, 0]);
      parts.blob(front, BELLY, [.015, .01, .022], [0, -.115, .008]);
      this.fronts.push(front);
      const hind = new THREE.Group(); hind.position.set(side * .062, -.045, 0); hips.add(hind);
      parts.blob(hind, fur, [.022, .017, .08], [0, -.012, .06]);
      this.hinds.push(hind);
    }
    bakeCritter(this.root);
  }

  apply(a: RabbitAction) {
    const t = a.idle + this.seed;
    const sit = mix(sitPose(a.previous, t), sitPose(a.mood, t), smooth(a.moodTime / .45));
    const p = mix(hopPose(a.hop, a.bolt), sit, a.settle);
    // Crouching sinks the body onto planted feet, and tipping the nose down would drive the forepaws into the ground, so the hips ride up to meet them.
    this.hips.position.y = RABBIT_HIP_HEIGHT + p.lift - p.crouch * .03 + Math.max(0, Math.sin(p.pitch)) * .15;
    this.hinds.forEach(leg => { leg.position.y = -.045 + p.crouch * .03; });
    this.fronts.forEach(leg => { leg.position.y = .04 + p.crouch * .03; });
    this.hips.rotation.x = p.pitch;
    this.neck.rotation.set(p.head, p.yaw, 0);
    this.ears.forEach((ear, index) => {
      const side = index ? 1 : -1;
      // Each ear flicks on its own beat.
      ear.rotation.set(p.ear + Math.sin(t * (index ? 1.3 : .9) + index * 2) * .06 * a.settle, 0, -side * p.splay);
    });
    this.muzzle.scale.set(.032, .028 * (1 + p.nose * .28), .035 * (1 + p.chew * .1));
    this.muzzle.position.y = -.012 - p.chew * .006;
    this.fronts.forEach(leg => { leg.rotation.x = p.front; });
    // Sitting, the hind feet stay flat on the ground however the body tilts above them.
    this.hinds.forEach(leg => { leg.rotation.x = p.hind - p.pitch * a.settle; });
  }
}
